import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

// https://github.com/captec/capital/issues/3710
const API_DATE_FORMAT = "YYYY-MM-DD H:mm:ss.SSSSS";

// let's make so all or almost all the dates in our app are in the same format using this function
// date is from backend in the following format: created_at: 2020-05-20 18:23:21.000000
export const formatDate = (
    date: string,
    {
        asOfTime = false,
        hideTime = false,
        parseAsUTC = true,
        dateFormat = API_DATE_FORMAT,
        outputFormat,
    }: {
        asOfTime?: boolean;
        hideTime?: boolean;
        parseAsUTC?: boolean;
        dateFormat?: string;
        outputFormat?: string;
    } = {},
) => {
    const parsedDate = dayjs(date, dateFormat);

    const correctDate = parseAsUTC ? parsedDate.utc(true).local() : parsedDate;

    if (outputFormat) {
        return correctDate.format(outputFormat);
    }

    if (hideTime) {
        return correctDate.format("MMM D, YYYY");
    } else if (asOfTime) {
        return correctDate.format("MMM YYYY");
    }

    return correctDate.format("MMM D, YYYY, h:mm A");
};

/**
 * Create a human readable time difference that counts back from now.  Eg `Just now`, `1 hour ago`, ect
 * @param date
 * @param dateFormat
 * @param parseAsUTC
 */
export const formatDateFromNow = (
    date: string,
    {
        parseAsUTC = true,
        dateFormat = API_DATE_FORMAT,
    }: {
        parseAsUTC?: boolean;
        dateFormat?: string;
    } = {},
) => {
    const parsedDate = dayjs(date, dateFormat);

    // utc(true) necessary since it fixes safari issue
    const correctDate = parseAsUTC ? parsedDate.utc(true).local() : parsedDate;

    return correctDate.fromNow();
};

/**
 * Format a date string (MM/DD/YYYY) to readable format (MMM D, YYYY)
 * @param date - Date string in MM/DD/YYYY format (e.g., "12/10/2025")
 * @returns Formatted date string (e.g., "Dec 10, 2025") or "-" if invalid
 */
export const formatDateString = (date: string | undefined | null): string => {
    if (!date || date === "-") {
        return "-";
    }

    const parsedDate = dayjs(date, "MM/DD/YYYY");

    if (!parsedDate.isValid()) {
        return "-";
    }

    return parsedDate.format("MMM D, YYYY");
};

/**
 * i.e.:
 *   10.192  => $10.19
 *   1200400 => $1.2 M
 */
function money(input: number | string, short = true) {
    if (input === null || input === undefined) return "";
    const amount = parseFloat(input as string);
    const magnitude = Math.abs(amount);

    // Normal formatting for amounts under $100
    if (magnitude < 100) {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    }

    // Remove cents for amounts over $100
    if (magnitude < 1000000 || !short) {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    }

    // Format amounts up to 100 million with M to indicate millions, use up two fraction digits for
    // thousands
    if (magnitude < 100000000) {
        const output = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount / 1000000);

        return `${output}M`;
    }

    // Format amounts up to 1 billion with M to indicate millions, without fraction digits
    if (magnitude < 1000000000) {
        const output = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(Math.trunc(amount / 1000000));
        return `${output}M`;
    }

    // Format amounts up to 1 trillion with B to indicate billions without any fraction digits
    if (magnitude < 1000000000000) {
        const output = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(Math.trunc(amount / 1000000000));

        return `${output}B`;
    }

    const output = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount / 1000000000000);
    return `${output}T`;
}

export function longFormMoney(
    value: number | undefined | null | string,
): string {
    if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "N/A"
    ) {
        return "-";
    }

    // Convert to number if string with strict validation
    let numericValue: number;
    if (typeof value === "string") {
        const str = value.trim();
        if (!/^-?\d+(\.\d+)?$/.test(str)) {
            return "-";
        }
        numericValue = Number(str);
    } else {
        numericValue = value as number;
    }

    // Check if conversion resulted in NaN
    if (isNaN(numericValue)) {
        return "-";
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(numericValue);
}

export function formatPercentage(
    value: number | undefined | null | string,
    options: {
        decimals?: number;
        showSign?: boolean;
        fallback?: string;
        asDecimal?: boolean;
        negativeStyle?: "standard" | "parentheses";
        isPercentage?: boolean;
    } = {},
): string {
    const {
        decimals = 1,
        showSign = false,
        fallback = "-",
        asDecimal = true,
        negativeStyle = "parentheses",
        isPercentage = false,
    } = options;

    if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === "N/A"
    ) {
        return fallback;
    }

    // Convert to number if string with strict validation
    let numericValue: number;
    if (typeof value === "string") {
        const str = value.trim();
        if (!/^-?\d+(\.\d+)?$/.test(str)) {
            return fallback;
        }
        numericValue = Number(str);
    } else {
        numericValue = value as number;
    }

    // Check if conversion resulted in NaN
    if (isNaN(numericValue)) {
        return fallback;
    }

    // If already a percentage (e.g., 289.34355 means 289.3%), format directly without multiplying
    if (isPercentage) {
        const roundedValue =
            Math.round(numericValue * Math.pow(10, decimals)) /
            Math.pow(10, decimals);

        if (numericValue < 0 && negativeStyle === "parentheses") {
            const positiveFormatted = new Intl.NumberFormat("en-US", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(Math.abs(roundedValue));

            if (roundedValue === 0) {
                return `${positiveFormatted}%`;
            }
            return `(${positiveFormatted}%)`;
        }

        const formatted = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(roundedValue);

        if (showSign && roundedValue > 0) {
            return `+${formatted}%`;
        }

        return `${formatted}%`;
    }

    // Original logic for decimal values (e.g., 0.289 means 28.9%)
    const percentValue = asDecimal ? numericValue : numericValue / 100;

    const roundedValue =
        Math.round(percentValue * 100 * Math.pow(10, decimals)) /
        Math.pow(10, decimals);

    if (numericValue < 0 && negativeStyle === "parentheses") {
        const positiveFormatted = new Intl.NumberFormat("en-US", {
            style: "percent",
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(Math.abs(percentValue));

        if (roundedValue === 0) {
            return positiveFormatted;
        }
        return `(${positiveFormatted})`;
    }

    const formatted = new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(percentValue);

    if (showSign && roundedValue > 0) {
        return `+${formatted}`;
    }

    return formatted;
}

export { money };
